// ReceiptsPage — a single view: the "to review" queue on top (always-on, every
// pending receipt regardless of month) and the month-paginated "submitted"
// archive below. Polls the queue while anything is still digitizing.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useReceiptsStore, { SEARCH_LIMIT } from '../stores/useReceiptsStore';
import useUserStore from '../stores/useUserStore';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import UploadReceiptModal from './UploadReceiptModal';
import StatusPill from './StatusPill';
import ReceiptThumb from './ReceiptThumb';
import useNudges, { nudgeCaption, NudgeSelectedButton } from './useNudges';
import MissingCharges from './MissingCharges';
import Pager from './Pager';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import { FilterBar, FilterBreak, FilterSearch } from '../components/FilterBar';
import SegTrack from '../components/ui/SegTrack';
import { Field, DateRange } from '../components/ui/Field';
import { Checkbox } from '../components/ui/Checkbox';
import { Tag } from '../components/ui/Tag';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { ListRow } from '../components/ui/ListRow';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { Panel } from '../components/ui/Panel';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';
import type { MissingCharge, Receipt } from './types';

const daysAgo = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

const SEARCH_ID = 'receipts-search';

// The page's three views, kept in the URL as ?tab= so a reload or a shared link
// lands on the same one. Absent or unknown reads as 'review'.
type Tab = 'review' | 'missing' | 'submitted';
const TABS: readonly string[] = ['review', 'missing', 'submitted'];

// The DOM id a queue/archive row carries, so the failed-reads banner can send
// the reader to the row it is talking about.
const rowDomId = (id: number) => `receipt-row-${id}`;

// What to call a receipt whose read failed. A failed read often got far enough
// to name the vendor; when it did not, the file the person picked is the thing
// they recognize.
const receiptLabel = (r: Receipt) => r.vendor_name || r.original_filename || 'this receipt';

// A queue row is "clean" enough to one-tap confirm without opening the detail
// page. List rows don't carry likely_duplicate_of (that join is detail-only,
// see receipts-service.js listSelect vs detailSelect) so the Tier 2 soft-dup
// check rides on needs_attention, which already folds in 'possible_duplicate'.
const isCleanRow = (r: Receipt) =>
  r.status === 'needs_review' &&
  !r.needs_attention &&
  !!r.vendor_name &&
  r.total != null &&
  r.category_id != null &&
  r.card_id != null &&
  !r.detected_card_owner_id;

// 'YYYY-MM' month helpers (lexicographic compare is correct for this format).
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const CURRENT_MONTH = monthKey(new Date());
const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
};
const shiftMonth = (key: string, delta: number) => {
  const [y, m] = key.split('-').map(Number);
  return monthKey(new Date(y, m - 1 + delta, 1));
};

export default function ReceiptsPage() {
  const {
    toReview, toReviewOther, submitted, results, searchError, loadingQueue, loadingMonth, loadingSearch,
    fetchQueue, fetchMonth, searchReceipts, clearSearch, submitReceipt, submitClean, startFlow,
    fetchMissingCharges, missingCharges,
  } = useReceiptsStore();
  const history = useHistory();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const user = useUserStore((s: any) => s.user);
  const canFinancial = !!user?.can_financial;
  const [showUpload, setShowUpload] = useState(false);
  // Set when the dialog opened from a row in Charges without a receipt.
  const [uploadFor, setUploadFor] = useState<MissingCharge | null>(null);
  const location = useLocation();

  // Deep link: /receipts?scan=1 (PWA long-press shortcut, mobile menu) opens the
  // capture modal on arrival. The camera itself still needs one tap — browsers
  // only open the OS camera sheet from a user gesture. replace() strips the
  // param so back/refresh don't re-open the modal.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('scan')) {
      setUploadFor(null);
      setShowUpload(true);
      history.replace('/receipts');
    }
  }, [location.search]); // history intentionally excluded
  const [month, setMonth] = useState(CURRENT_MONTH);
  // Financial-only "Everyone" scope (server-enforced). Non-financial users are pinned to 'mine'.
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const effScope: 'mine' | 'all' = canFinancial ? scope : 'mine';

  // Missing receipts are the caller's own cards, so Everyone has no such tab.
  const tabParam = new URLSearchParams(location.search).get('tab') || '';
  const requestedTab: Tab = TABS.includes(tabParam) ? (tabParam as Tab) : 'review';
  const tab: Tab = requestedTab === 'missing' && effScope !== 'mine' ? 'review' : requestedTab;
  const setTab = (next: string) => {
    const p = new URLSearchParams(location.search);
    if (next === 'review') p.delete('tab'); else p.set('tab', next);
    const q = p.toString();
    history.replace(`${location.pathname}${q ? `?${q}` : ''}`);
  };

  // Nudge is a check, then one button (financial-only, so only fetch the
  // "recently nudged" map when this user could ever see that rollup).
  const { recent: nudgeRecent, sending: nudgeSending, progress: nudgeProgress, sendSelected } = useNudges(canFinancial);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const togglePick = (userId: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    return next;
  });

  const queueRef = useRef<Receipt[]>([]);
  // Rows I scanned for others digest too (retake, rescan), so the poll and the diff watch both lists.
  const queued = useMemo(() => [...toReview, ...toReviewOther], [toReview, toReviewOther]);
  queueRef.current = queued;

  // Transient "detection finished" check when a queue row flips processing → needs_review,
  // plus a quiet note when a still-digesting row vanishes entirely (routed to its
  // cardholder under the communal-scanning rules — see docs/features/receipt-reconciliation-module.md).
  const prevStatus = useRef<Record<number, string>>({});
  // A read that fails is announced exactly once, and how depends on whether this
  // session watched it happen. Failing on screen gets a toast; already-failed on
  // arrival (or on a later visit) has nobody to interrupt, so it rolls up into
  // the banner instead. Toasting on load would replay old news every refresh,
  // and a banner over a row that just flashed would say the same thing twice.
  const announcedFailures = useRef<Set<number>>(new Set());
  const dismissedFailures = useRef<Set<number>>(new Set());
  const [silentFailures, setSilentFailures] = useState<number[]>([]);
  const flashTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [justDone, setJustDone] = useState<Set<number>>(() => new Set());
  // A scope toggle (Mine/Everyone) swaps in a wholesale different dataset — rows
  // "disappearing" then are a scope artifact, not real routing. Skip the very next
  // diff after effScope changes so it only resyncs prevStatus without flagging it.
  const skipNextDiff = useRef(false);
  useEffect(() => { skipNextDiff.current = true; }, [effScope]);
  // Clear every pending flash timer on unmount only — NOT on each toReview change,
  // or a poll landing mid-flash would cancel the removal and leave a badge stuck.
  useEffect(() => () => flashTimers.current.forEach(clearTimeout), []);
  useEffect(() => {
    const skipDiff = skipNextDiff.current;
    skipNextDiff.current = false;

    const newlyDone: number[] = [];
    const newlyFailed: Receipt[] = [];
    const seen = new Set<number>();
    queued.forEach((r) => {
      const prev = prevStatus.current[r.id];
      const wasDigesting = prev === 'processing' || prev === 'uploaded';
      if (!skipDiff && wasDigesting && r.status === 'needs_review') newlyDone.push(r.id);
      if (!skipDiff && wasDigesting && r.status === 'processing_failed') newlyFailed.push(r);
      prevStatus.current[r.id] = r.status;
      seen.add(r.id);
    });

    // Rows still digesting last poll that are simply gone now (no status at all)
    // routed away rather than finishing here. Best-effort: fires the moment a row
    // disappears rather than waiting an extra poll to rule out reappearance — a
    // routed receipt bouncing back within one 3.5s tick isn't a real scenario
    // under the current digest-once routing.
    let routedAway = 0;
    Object.keys(prevStatus.current).forEach((key) => {
      const id = Number(key);
      if (seen.has(id)) return;
      if (!skipDiff && (prevStatus.current[id] === 'processing' || prevStatus.current[id] === 'uploaded')) routedAway += 1;
      delete prevStatus.current[id];
    });
    if (routedAway) {
      toast.info(routedAway === 1 ? '1 receipt routed to its cardholder' : `${routedAway} receipts routed to their cardholders`);
    }

    // Announce the reads that failed while this page watched. The toast carries
    // no link: the row is already on screen, wearing its red pill.
    // One toast per poll, not per receipt: the toast stack keeps the last four and
    // drops the rest without a trace, and a dropped toast here would mark a receipt
    // announced that nobody was told about.
    newlyFailed.forEach((r) => announcedFailures.current.add(r.id));
    if (newlyFailed.length === 1) {
      toast.error(`Couldn't read ${receiptLabel(newlyFailed[0])}. Retake the photo or fill it in by hand.`);
    } else if (newlyFailed.length > 1) {
      toast.error(`Couldn't read ${newlyFailed.length} receipts. Retake the photos or fill them in by hand.`);
    }

    // Whatever is left failed somewhere this session never saw. Computed here
    // rather than in a render-time memo so a row toasted a line above is already
    // out of it, and one poll can't both flash and band the same receipt.
    const unannounced = queued
      .filter((r) => r.status === 'processing_failed'
        && !announcedFailures.current.has(r.id)
        && !dismissedFailures.current.has(r.id))
      .map((r) => r.id);
    setSilentFailures((prev) =>
      (prev.length === unannounced.length && prev.every((id, i) => id === unannounced[i]) ? prev : unannounced));

    if (!newlyDone.length) return;
    setJustDone((s) => { const n = new Set(s); newlyDone.forEach((id) => n.add(id)); return n; });
    newlyDone.forEach((id) => {
      const t = setTimeout(() => setJustDone((s) => { const n = new Set(s); n.delete(id); return n; }), 3200);
      flashTimers.current.push(t);
    });
  }, [queued]);

  // Dismiss is per session and per receipt: these ones have been seen, a read
  // that fails after this still gets its say.
  const dismissFailures = () => {
    silentFailures.forEach((id) => dismissedFailures.current.add(id));
    setSilentFailures([]);
  };

  // "Show it" names a row that may sit on a later page of its list. The id goes to
  // the pagers as `reveal`, which jump to that page in the same render; the effect
  // then scrolls to the row now that it is in the DOM, and lets the pagers go.
  const [revealId, setRevealId] = useState<number | null>(null);
  const showFirstFailure = () => setRevealId(silentFailures[0]);
  useEffect(() => {
    if (revealId == null) return;
    const el = document.getElementById(rowDomId(revealId));
    if (el) {
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      (el.querySelector<HTMLElement>('a') ?? el).focus({ preventScroll: true });
    }
    setRevealId(null);
  }, [revealId]);
  const revealRow = revealId != null ? (r: Receipt) => r.id === revealId : undefined;

  const refreshQueue = () => fetchQueue(effScope).catch(() => {});
  // Charges without a receipt are the caller's own cards, so only Mine shows them.
  // Loaded / failed, so the Missing receipts tab can tell "none" from "not yet" and "couldn't".
  const [missingState, setMissingState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const refreshMissing = () => fetchMissingCharges()
    .then(() => setMissingState('ready'))
    .catch(() => setMissingState((s) => (s === 'ready' ? s : 'failed')));
  const unansweredMissing = missingCharges.filter((c) => !c.response).length;
  useEffect(() => {
    if (effScope === 'mine') refreshMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effScope]);
  // The list changes on the bookkeeper's side too (a match at her next rematch, an
  // exception), with nothing here to notice. Opening the tab, or coming back to
  // the page while it is open, re-reads it; that is as fresh as it needs to be.
  useEffect(() => {
    if (tab !== 'missing') return;
    refreshMissing();
    const onBack = () => { if (document.visibilityState === 'visible') refreshMissing(); };
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => {
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  // A batch calls this once per file; only an upload for one charge changes the missing list.
  const handleUploaded = (forCharge?: boolean) => {
    refreshQueue();
    if (forCharge) refreshMissing();
  };

  // Split the always-on queue: clean rows are safe to one-tap confirm, the rest
  // still need a human look (mirrors ReconcilePage's Confident/Needs-your-eyes
  // idiom). Only rendered for 'mine' — the Everyone view stays a flat list.
  // "Review all" flow snapshot: the Needs-your-eyes rows that are actually
  // reviewable right now. Still-digesting rows (processing/uploaded) have nothing
  // to act on yet, so they're excluded from the deck.
  const splitQueue = (rows: Receipt[]) => {
    const ready: Receipt[] = [];
    const eyes: Receipt[] = [];
    rows.forEach((r) => (isCleanRow(r) ? ready : eyes).push(r));
    const deck = eyes.filter((r) => r.status !== 'processing' && r.status !== 'uploaded').map((r) => r.public_id);
    return { ready, eyes, deck };
  };
  // My own queue splits by who scanned it: the ones I scanned, and the ones others scanned for me.
  const scannedQueue = useMemo(() => splitQueue(toReview.filter((r) => r.uploaded_by === user?.id)), [toReview, user?.id]);
  const receivedQueue = useMemo(() => splitQueue(toReview.filter((r) => r.uploaded_by !== user?.id)), [toReview, user?.id]);
  const otherQueue = useMemo(() => splitQueue(toReviewOther), [toReviewOther]);
  const handleReviewAll = (deck: string[]) => {
    if (!deck.length) return;
    startFlow(deck);
    history.push(`/receipts/${deck[0]}`);
  };

  // Per-row optimistic state for the "Looks good" quick-confirm button. Pruned
  // once refreshQueue's fetch actually drops the row (it moved off needs_review
  // server-side) rather than cleared eagerly, so "Submitting…" holds through the refetch.
  const [submittingIds, setSubmittingIds] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    setSubmittingIds((s) => {
      if (s.size === 0) return s;
      const stillQueued = new Set([...toReview, ...toReviewOther].map((r) => r.id));
      const next = new Set<number>();
      let changed = false;
      s.forEach((id) => { if (stillQueued.has(id)) next.add(id); else changed = true; });
      return changed ? next : s;
    });
  }, [toReview, toReviewOther]);

  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const handleConfirmOne = async (r: Receipt) => {
    setSubmittingIds((s) => new Set(s).add(r.id));
    try {
      await submitReceipt(r.public_id);
      refreshQueue();
    } catch (err: any) {
      toast.error(err?.message || 'Could not submit this receipt');
      setSubmittingIds((s) => { const n = new Set(s); n.delete(r.id); return n; });
    }
  };

  type QueueScope = 'mine' | 'scanned' | 'received' | 'uploaded';
  const handleConfirmAll = async (scope: QueueScope, n: number) => {
    if (!n) return;
    const ok = await confirmDialog({
      title: `Confirm ${n} receipts?`,
      message: scope === 'uploaded'
        ? 'These read cleanly: totals reconcile, categories are confirmed, and cards match. You scanned them for other cardholders. Confirming submits them for reconciliation.'
        : scope === 'received'
        ? 'These read cleanly: totals reconcile, categories are confirmed, and cards match. Someone else scanned them for you. Confirming submits them as yours for reconciliation.'
        : 'These read cleanly: totals reconcile, categories are confirmed, and cards match. Confirming submits them as yours for reconciliation.',
      confirmLabel: `Confirm all ${n}`,
    });
    if (!ok) return;
    setBulkSubmitting(true);
    try {
      const res = await submitClean(scope);
      const base = `Submitted ${res.submitted} receipt${res.submitted === 1 ? '' : 's'}`;
      toast.success(res.skipped.length ? `${base} (${res.skipped.length} skipped)` : base);
      refreshQueue();
    } catch (err: any) {
      toast.error(err?.message || 'Could not submit receipts');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // Initial load + poll the queue while anything is digitizing; refetch on scope change.
  useEffect(() => {
    fetchQueue(effScope).catch(() => {});
    const t = setInterval(() => {
      if (queueRef.current.some((r) => r.status === 'processing' || r.status === 'uploaded')) fetchQueue(effScope).catch(() => {});
    }, 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effScope]);

  // (Re)load the submitted archive whenever the month or scope changes.
  useEffect(() => {
    fetchMonth(month, effScope).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, effScope]);

  // Search. Any effective filter swaps the queue + archive for one all-time result
  // list; clearing them brings the normal view back. Text needs two characters
  // before it counts (one letter would match nearly everything), an amount must
  // read as a number once "$" and "," are dropped, and the dates count as typed.
  const [query, setQuery] = useState('');
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const amountValue = amount.replace(/[$,\s]/g, '');
  const amountInvalid = amountValue !== '' && !Number.isFinite(Number(amountValue));
  const rangeInvalid = !!(from && to && from > to);
  const search = useMemo(() => {
    const q = query.trim();
    return {
      q: q.length >= 2 ? q : '',
      amount: amountValue && !amountInvalid ? amountValue : '',
      from,
      to,
    };
  }, [query, amountValue, amountInvalid, from, to]);
  const searching = !!(search.q || search.amount || search.from || search.to);
  const hasSearchInput = !!(query || amount || from || to);
  const clearAll = () => {
    setQuery(''); setAmount(''); setFrom(''); setTo('');
    document.getElementById(SEARCH_ID)?.focus(); // the Clear button unmounts itself; keep focus on the page
  };
  // Capture phase, on the bar: Escape in any of its fields clears every filter, and
  // runs before the search field's own Escape (which clears only its text).
  const escClears = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && (e.target as HTMLElement).tagName === 'INPUT') clearAll();
  };
  // True from the keystroke until the debounced request settles, so the empty state
  // cannot show for a search that has not been sent yet.
  const [searchPending, setSearchPending] = useState(false);
  // A scope toggle swaps datasets: drop the other scope's rows at once rather than
  // leaving someone else's receipts on screen, unlabeled, until the refetch lands.
  useEffect(() => { clearSearch(); }, [effScope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!searching) { clearSearch(); setSearchPending(false); return; }
    setSearchPending(true);
    const t = setTimeout(() => {
      searchReceipts(search, effScope).catch(() => {}).finally(() => setSearchPending(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searching, effScope]);

  // Accountability rollup (Everyone view): who has unreviewed receipts, oldest first-ish.
  // Keyed by effective owner user id (not name) so nudging targets the right person.
  const unreviewedByOwner = useMemo(() => {
    if (effScope !== 'all') return [] as { userId: string; name: string; count: number; oldest: string }[];
    const m = new Map<string, { userId: string; name: string; count: number; oldest: string }>();
    toReview.filter((r) => r.status === 'needs_review').forEach((r) => {
      const userId = r.owner_user_id || r.uploaded_by;
      const name = r.owner_name || r.uploaded_by_name || 'Unknown';
      const cur = m.get(userId) || { userId, name, count: 0, oldest: r.created_at };
      cur.count += 1;
      if (r.created_at < cur.oldest) cur.oldest = r.created_at;
      m.set(userId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [effScope, toReview]);

  // A read that failed has no vendor and no total, so three of them read as the
  // same row. The picture, the file's name and the reason tell them apart; the
  // review page it opens carries the Retake button.
  const renderFailedRow = (r: Receipt) => (
    <ListRow
      key={r.id}
      id={rowDomId(r.id)}
      to={`/receipts/${r.public_id}`}
      tone="danger"
      leading={<ReceiptThumb pid={r.public_id} mime={r.mime_type} />}
      title={(
        <>
          {r.vendor_name || r.original_filename || 'Receipt'}
          {(effScope === 'all' || (r.owner_user_id && r.owner_user_id !== user?.id)) && r.owner_name && <span className="text-xs font-normal text-on-surface-variant"> · {r.owner_name}</span>}
        </>
      )}
      meta={(
        <>
          <span className="block truncate text-red-300/90">Couldn't read this one. Open it to retake the photo.</span>
          {r.digitization_error && <span className="block truncate text-[11px]">{r.digitization_error}</span>}
        </>
      )}
      trailing={<StatusPill status={r.status} className="shrink-0" />}
    />
  );

  const renderRow = (r: Receipt, showConfirm = false) => {
    if (r.status === 'processing_failed') return renderFailedRow(r);
    const detecting = r.status === 'processing' || r.status === 'uploaded';
    const done = !detecting && justDone.has(r.id);
    return (
      <ListRow
        key={r.id}
        id={rowDomId(r.id)}
        to={`/receipts/${r.public_id}`}
        title={(
          <>
            {r.vendor_name || (r.status === 'processing' ? 'Detecting…' : 'Unknown vendor')}
            <Appear show={!!r.needs_attention} className="ml-1"><Tag tone="warn" size="sm">Needs a look</Tag></Appear>
            {(effScope === 'all' || (r.owner_user_id && r.owner_user_id !== user?.id)) && r.owner_name && <span className="text-xs font-normal text-on-surface-variant"> · {r.owner_name}</span>}
            {effScope !== 'all' && r.uploaded_by !== user?.id && r.status !== 'submitted' && r.uploaded_by_name && <span className="text-xs font-normal text-on-surface-variant"> · from {r.uploaded_by_name}</span>}
          </>
        )}
        meta={<>{r.receipt_date || '—'} · <span className="font-mono tabular-nums text-on-surface">{money(r.total)}</span>{r.category_label ? ` · ${r.category_label}` : ''}{r.event_name ? ` · ${r.event_name}` : ''}</>}
        trailing={detecting ? (
          <Tag className="shrink-0 gap-1.5">
            <LogoSpinner size="inline" />
            detecting
          </Tag>
        ) : done ? (
          <Tag tone="good" className="shrink-0 gap-1">
            <span className="font-bold">✓</span> detection finished
          </Tag>
        ) : showConfirm ? undefined : (
          <StatusPill status={r.status} className="shrink-0" />
        )}
        actions={!detecting && !done && showConfirm ? (
          <button type="button" disabled={submittingIds.has(r.id) || bulkSubmitting}
                  onClick={() => handleConfirmOne(r)}
                  className={`${buttonClass('quiet', 'sm')} shrink-0`}>
            {submittingIds.has(r.id) ? 'Submitting…' : 'Looks good'}
          </button>
        ) : undefined}
      />
    );
  };

  const rowList = (page: Receipt[], showConfirm = false) => (
    <ul className="m-0 grid list-none gap-2 p-0">{page.map((r) => renderRow(r, showConfirm))}</ul>
  );

  const skeleton = (
    <SkeletonGroup label="Loading receipts" className="space-y-2">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-[58px] rounded-panel" />
      ))}
    </SkeletonGroup>
  );

  // One "Ready to confirm" / "Needs your eyes" pair. `scope` says whose rows these are,
  // so "Confirm all" acts on exactly the rows on screen.
  const renderQueueGroup = (q: { ready: Receipt[]; eyes: Receipt[]; deck: string[] }, scope: QueueScope) => (
    <div>
      <Reveal show={q.ready.length > 0}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-green-300">Ready to confirm</span>
            <Tag tone="good" size="sm" className="tabular-nums">{q.ready.length}</Tag>
            <Appear show={q.ready.length >= 2} className="ml-auto">
              <button onClick={() => handleConfirmAll(scope, q.ready.length)} disabled={bulkSubmitting}
                      className={buttonClass('tonal', 'sm')}>
                Confirm all {q.ready.length}
              </button>
            </Appear>
          </div>
          <Pager items={q.ready} label="Pages of receipts ready to confirm" reveal={revealRow}>{(page) => (
            rowList(page, true)
          )}</Pager>
        </div>
      </Reveal>
      <Reveal show={q.eyes.length > 0}>
        <div className={q.ready.length > 0 ? 'pt-5' : undefined}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-on-surface">Needs your eyes</span>
            <Tag size="sm" className="tabular-nums">{q.eyes.length}</Tag>
            <Appear show={q.deck.length >= 1} className="ml-auto">
              <button onClick={() => handleReviewAll(q.deck)}
                      className={buttonClass('tonal', 'sm')}>
                Review all {q.deck.length}
              </button>
            </Appear>
          </div>
          <Pager items={q.eyes} label="Pages of receipts that need your eyes" reveal={revealRow}>{(page) => (
            rowList(page)
          )}</Pager>
        </div>
      </Reveal>
    </div>
  );

  // Financial surfaces (Reconcile/Ledger/Insights) are reached via the nav
  // and the Reconcile library, not from here — this page is every employee's.
  return (
    <PageLayout width="narrow"
      kind="workspace"
      className="pt-4 lg:pt-6"
      header={(
        <PageHeader
          className="mb-6"
          title="Receipts"
          primary={(
            <PrimaryAction onClick={() => { setUploadFor(null); setShowUpload(true); }}>
              + Add receipt
            </PrimaryAction>
          )}
        />
      )}
    >
      {/* The three views. Hidden, not removed, while searching: the bar below must not
          jump up under the cursor the moment someone types. */}
      <div className={`mb-4 ${searching ? 'invisible' : ''}`}>
        <SegTrack
          label="Which receipts to show"
          value={tab}
          onChange={setTab}
          options={[
            { key: 'review', label: 'To review', badge: toReview.length + toReviewOther.length },
            ...(effScope === 'mine' ? [{ key: 'missing', label: 'Missing receipts', badge: unansweredMissing }] : []),
            { key: 'submitted', label: 'Submitted' },
          ]}
        />
      </div>

      {/* Search: vendor / person / note text, an exact amount, a date range. Plus,
          financial-only, the Mine / Everyone view. */}
      <div className="mb-5" onKeyDownCapture={escClears}>
        <FilterBar label="Find receipts">
          {canFinancial && (
            <>
              <span className="text-xs text-on-surface-variant">View:</span>
              <SegTrack
                label="Whose receipts to show"
                value={scope}
                onChange={(k) => setScope(k as 'mine' | 'all')}
                options={[{ key: 'mine', label: 'Mine' }, { key: 'all', label: 'Everyone' }]}
              />
              <FilterBreak />
            </>
          )}
          <FilterSearch
            id={SEARCH_ID}
            value={query}
            onChange={setQuery}
            label="Search receipts"
            placeholder={effScope === 'all' ? 'Search vendor, person or note' : 'Search vendor or note'}
          />
          <Tooltip label={amountInvalid ? 'Not a number' : ''}>
            <Field type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
                   placeholder="Amount" aria-label="Amount" error={amountInvalid}
                   className="!w-24 font-mono tabular-nums text-right" />
          </Tooltip>
          <DateRange label="Date range" from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
          <Appear show={hasSearchInput}>
            <button type="button" onClick={clearAll}
                    className={buttonClass('quiet', 'sm')}>
              Clear
            </button>
          </Appear>
        </FilterBar>
      </div>

      {/* Reads that failed before this session arrived. Above the accountability
          rollup on purpose: a receipt nobody can read is stuck harder than one
          nobody has reviewed. */}
      <Reveal show={!searching && tab === 'review' && silentFailures.length > 0}>
        <div className="pb-3">
        <Alert
          onDismiss={dismissFailures}
          title={silentFailures.length === 1 ? "1 receipt couldn't be read" : `${silentFailures.length} receipts couldn't be read`}
          action={(
            <button type="button" onClick={showFirstFailure} className={buttonClass('tonal', 'md')}>
              {silentFailures.length === 1 ? 'Show it' : 'Show the first one'}
            </button>
          )}
        >
          {silentFailures.length === 1
            ? 'Open it to retake the photo or fill it in by hand.'
            : 'Open each one to retake the photo or fill it in by hand.'}
        </Alert>
        </div>
      </Reveal>

      {/* Accountability rollup — who still owes a review (Everyone view only) */}
      <Reveal show={!searching && tab === 'review' && effScope === 'all' && unreviewedByOwner.length > 0}>
        <div className="pb-6">
        <Panel
          padding="sm"
          title="Unreviewed by person"
          actions={(
            <NudgeSelectedButton
              count={unreviewedByOwner.filter((o) => picked.has(o.userId)).length}
              sending={nudgeSending} progress={nudgeProgress}
              onClick={async () => {
                const targets = unreviewedByOwner.filter((o) => picked.has(o.userId));
                const { sentIds } = await sendSelected(targets);
                if (!sentIds.length) return;
                setPicked((prev) => {
                  const next = new Set(prev);
                  sentIds.forEach((id) => next.delete(id));
                  return next;
                });
              }} />
          )}
        >
          <div className="flex flex-wrap gap-2">
            {unreviewedByOwner.map((o) => (
              <Checkbox key={o.userId} size="sm" className="np-state rounded-full bg-surface-container-high py-1 pl-2 pr-2.5 text-xs text-on-surface-variant"
                checked={picked.has(o.userId)} disabled={nudgeSending}
                aria-label={`Include ${o.name}`}
                onChange={() => togglePick(o.userId)}
                label={(
                  <span className="inline-flex flex-wrap items-center gap-x-2 text-xs">
                    <span>
                      {o.name}: <span className="font-semibold tabular-nums text-on-surface">{o.count}</span>
                      <span className="text-on-surface-variant"> · oldest {daysAgo(o.oldest)}d</span>
                    </span>
                    <Appear show={!!nudgeRecent[o.userId]} className="text-on-surface-variant whitespace-nowrap">
                      Nudged {nudgeRecent[o.userId] ? nudgeCaption(nudgeRecent[o.userId]) : ''}
                    </Appear>
                  </span>
                )} />
            ))}
          </div>
        </Panel>
        </div>
      </Reveal>

      {searching ? (
        /* Results — every month and status, newest expense first, capped server-side */
        <section>
          <div className="flex items-baseline gap-2 mb-2.5" aria-live="polite">
            <h2 className="text-sm font-semibold text-on-surface">Results</h2>
            <Appear show={!searchPending && !loadingSearch && !searchError}>
              <span className="text-xs tabular-nums text-on-surface-variant">({results.length}{results.length >= SEARCH_LIMIT ? '+' : ''})</span>
            </Appear>
          </div>
          <FadeIn key={(searchPending || loadingSearch) && results.length === 0 ? 'loading' : searchError ? 'error' : rangeInvalid ? 'range' : results.length === 0 ? 'none' : 'results'}>
          {(searchPending || loadingSearch) && results.length === 0 ? skeleton
            : searchError ? (
              <Alert>Couldn't search: {searchError}</Alert>
            ) : rangeInvalid ? (
              <EmptyState variant="area">The date range starts after it ends.</EmptyState>
            ) : results.length === 0 ? (
              <EmptyState variant="area">Nothing matches. Try fewer words, or widen the dates.</EmptyState>
            ) : (
              <>
                <Pager items={results} label="Pages of search results">{(page) => (
                  rowList(page)
                )}</Pager>
                <Reveal show={results.length >= SEARCH_LIMIT}>
                  <p className="pt-3 text-center text-xs text-on-surface-variant">Showing the first {SEARCH_LIMIT}. Narrow the search to see the rest.</p>
                </Reveal>
              </>
            )}
          </FadeIn>
        </section>
      ) : (<>
      {/* My own card charges the statement shows with no receipt (Mine only) */}
      {tab === 'missing' && (
        <FadeIn key={missingState === 'loading' && missingCharges.length === 0 ? 'loading' : missingState === 'failed' && missingCharges.length === 0 ? 'failed' : missingCharges.length === 0 ? 'none' : 'list'}>
        {missingState === 'loading' && missingCharges.length === 0 ? skeleton
          : missingState === 'failed' && missingCharges.length === 0 ? (
            <Alert>Couldn't load your card charges. Reload the page to try again.</Alert>
          ) : missingCharges.length === 0 ? (
            <EmptyState title="No charges are waiting on a receipt">
              When your card statement shows a charge with no receipt, it lands here.
            </EmptyState>
          ) : (
            <MissingCharges
              embedded
              onUpload={(c) => { setUploadFor(c); setShowUpload(true); }}
              onChanged={refreshMissing}
            />
          )}
        </FadeIn>
      )}

      {/* To review — always-on queue (never scoped by month) */}
      {tab === 'review' && (
      <section className="mb-8">
        {/* The tab names the view and carries the count; the heading stays for readers only. */}
        <h2 className="sr-only">To review</h2>
        <FadeIn key={loadingQueue && toReview.length === 0 && toReviewOther.length === 0 ? 'loading' : toReview.length === 0 && toReviewOther.length === 0 ? 'none' : effScope === 'all' ? 'all' : 'mine'}>
        {loadingQueue && toReview.length === 0 && toReviewOther.length === 0 ? skeleton
          : toReview.length === 0 && toReviewOther.length === 0 ? (
            <EmptyState title="You're all caught up">
              Snap a receipt and it lands here for a quick review.
            </EmptyState>
          ) : effScope === 'all' ? (
            <Pager items={toReview} label="Pages of receipts to review" reveal={revealRow}>{(page) => (
              rowList(page)
            )}</Pager>
          ) : toReviewOther.length === 0 && receivedQueue.ready.length + receivedQueue.eyes.length === 0
            ? renderQueueGroup(scannedQueue, 'mine') : (
            <div className="space-y-6">
              {[
                { title: 'My Cards', q: scannedQueue, scope: 'scanned' as const },
                { title: 'Uploaded by others', q: receivedQueue, scope: 'received' as const },
                { title: 'Other Cards', q: otherQueue, scope: 'uploaded' as const },
              ].filter((g) => g.q.ready.length + g.q.eyes.length > 0).map((g) => (
                <div key={g.title}>
                  <h3 className="text-xs font-semibold text-on-surface-variant mb-2.5">{g.title}</h3>
                  {renderQueueGroup(g.q, g.scope)}
                </div>
              ))}
            </div>
          )}
        </FadeIn>
      </section>
      )}

      {/* Submitted — month-paginated archive */}
      {tab === 'submitted' && (
      <section>
        <div className="flex items-center justify-end mb-2.5">
          <h2 className="sr-only">Submitted</h2>
          <div className="flex items-center gap-1">
            <Tooltip label="Previous month">
              <IconButton size="sm" label="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))}>
                <IconChevronLeft />
              </IconButton>
            </Tooltip>
            <span className="text-xs font-medium text-on-surface min-w-[7.5rem] text-center">{monthLabel(month)}</span>
            <Tooltip label="Next month">
              <IconButton size="sm" label="Next month" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={month >= CURRENT_MONTH}>
                <IconChevronRight />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <FadeIn key={loadingMonth && submitted.length === 0 ? 'loading' : submitted.length === 0 ? 'none' : 'list'}>
        {loadingMonth && submitted.length === 0 ? skeleton
          : submitted.length === 0 ? (
            <EmptyState variant="area">No receipts submitted in {monthLabel(month)}.</EmptyState>
          ) : (
            <Pager items={submitted} resetKey={month} label="Pages of submitted receipts">{(page) => (
              rowList(page)
            )}</Pager>
          )}
        </FadeIn>
      </section>
      )}
      </>)}

      {showUpload && (
        <UploadReceiptModal
          forCharge={uploadFor ?? undefined}
          onClose={() => { setShowUpload(false); setUploadFor(null); }}
          onUploaded={handleUploaded}
        />
      )}
    </PageLayout>
  );
}
